/**
 * A project's roots (its repositories, and where each clone lives), split out of projects.ts in
 * AIOE 0.21.0 so projects.ts imports nothing the workbench does not mirror. This file leans on
 * fleet.ts for the host states a device reports (0.16.1): the host's state, its WSL distro and
 * the target's state, as the last registration gave them. The shapes are unchanged; the shared
 * index re-exports them as before.
 *
 *   PUT  /projects/v1/:id/roots/:rootId      owners: which node holds the canonical clone (`projectRootPutSchema`)
 *   GET  /projects/v1/:id                    `roots: ProjectRoot[]` beside the project and its members
 *
 * AIOE 0.34.0 (one project, many repositories, part 1; workbench Appendix G / D245). Only optional fields
 * and new refusals, so no schema version moves.
 *
 *   `name` (at most 100 characters) is a repository's name, words for people. It comes from the registration's
 *   workspace name the first time the root is seen; an owner changes it with `PUT …/roots/:rootId { name }`
 *   (a body of only `name` touches nothing else). `remoteKey` is `normaliseRemote(remote)`: the host lowercased,
 *   `.git` and credentials dropped, `git@host:a/b` read as `https://host/a/b`. It is the platform's, never sent.
 *   Every answer that carries a `rootId` carries `rootName` beside it (the stored name, else the live workspace's
 *   name, else the id); no surface shows a root id.
 *
 *   409 `same_remote` { rootId, rootName }   a registration or PUT whose remoteKey another root of the project has:
 *                                            "This is the same repository as <name>. Use that one, or remove it first."
 *
 *   DELETE /projects/v1/:id/roots/:rootId?confirm=1   owners. Without `confirm=1`, 409 `root_has_work` { open: RootOpenCounts }
 *                                            when anything is open. With it: open tasks are closed as cancelled ("<name> was
 *                                            removed from the project"), claims released with reason `root_removed`, the root
 *                                            leaves every `delegates` list (roll-ups recomputed), its live plans are abandoned,
 *                                            `root.removed` { root, rootName, cancelled, released } goes on the feed and
 *                                            `project.root.remove` is audited with the counts. Its boards and map stay readable
 *                                            (read-only) for 30 days, then the `projects.removed-roots` job drops them.
 *
 *   Board routes (`…/roots/:rootId/boards/…`): 404 `unknown_root` for a root the project does not have; 409 `root_removed`
 *   for a write to one removed in the last 30 days; 403 `root_not_held` for an agent's write (`x-aioe-actor`) on a root
 *   the calling device's last registration does not report: "<device> does not have <name>". A person's writes are not checked.
 */
import { z } from "zod";
import { classificationSchema, projectIdSchema } from "./projects.js";
import { workspaceHostStateSchema, workspaceTargetStateSchema } from "./fleet.js";
export const projectRootSchema = z.object({
    /** Safe in a URL path: the board routes carry it (`/projects/v1/:id/roots/:rootId/boards/:board`). */
    rootId: projectIdSchema,
    /** The node holding this clone, when it is on a node. */
    nodeId: z.string().max(100).optional(),
    /** A repository's name, in words (AIOE 0.34.0): from the registration, changed by an owner. */
    name: z.string().max(100).optional(),
    remote: z.string().max(500).optional(),
    /** `normaliseRemote(remote)` (AIOE 0.34.0), set by the platform: two roots of a project may not share one. */
    remoteKey: z.string().max(500).optional(),
    branch: z.string().max(200).optional(),
    /** The clone of record: the project node whose host keeps the board members attach to. */
    canonical: z.boolean().default(false),
    /** The device that last reported this root (fleet registration). */
    deviceId: z.string().optional(),
    /** When the root was last reported with its host running; a stopped host does not move it. */
    lastSeenAt: z.string().optional(),
    /** As the device last reported it (fleet registration): the host's state, its WSL distro, the target's state. */
    hostState: workspaceHostStateSchema.optional(),
    distro: z.string().max(200).optional(),
    targetState: workspaceTargetStateSchema.optional(),
    /** AIOE 0.40.0, optional: this repository's own classification; absent means it inherits the project's. Never weaker than the project's. */
    classification: classificationSchema.optional(),
});
/** A root whose host is stopped, failed or out of reach: listed, but not a sighting (its `lastSeenAt` stays when the host was last running). */
export function workspaceHostDown(w) {
    return w.hostState === "stopped" || w.hostState === "error" || w.hostState === "exited" || w.targetState === "stopped" || w.targetState === "unavailable";
}
export const projectRootPutSchema = projectRootSchema.omit({ rootId: true, remoteKey: true, classification: true, deviceId: true, lastSeenAt: true, hostState: true, distro: true, targetState: true });
/** A repository's name for people: the stored name, else the id (callers with more to go on fall back further before this). */
export const rootNameOf = (root, rootId) => root?.name?.trim() || rootId;
/** The words of a refusal that two roots are one repository. */
export const sameRemoteMessage = (name) => `This is the same repository as ${name}. Use that one, or remove it first.`;
/** How long a removed repository's boards and map stay readable before they are dropped. */
export const REMOVED_ROOT_KEEP_DAYS = 30;
//# sourceMappingURL=projectRoots.js.map